<template>
  <div class="container-fluid p-4">
    <form class="pm-header" @submit.prevent="loadTicker">
      <h5 class="mb-0 me-2">Post-Mortem</h5>
      <div class="btn-group btn-group-sm" role="group" aria-label="Chart">
        <button type="button" class="btn" :class="view === 'intraday' ? 'btn-secondary' : 'btn-outline-secondary'"
                @click="setView('intraday')">Intraday</button>
        <button type="button" class="btn" :class="view === 'macro' ? 'btn-secondary' : 'btn-outline-secondary'"
                @click="setView('macro')">Macro</button>
      </div>
      <input v-model.trim="ticker" class="form-control form-control-sm pm-ticker" placeholder="Ticker" required />
      <select v-if="transactions.length" v-model="txn" class="form-select form-select-sm pm-txn" @change="loadChart">
        <option value="">A session (no trade)</option>
        <option v-for="t in transactions" :key="t.e2etransactionid" :value="t.e2etransactionid">
          {{ t.first_trade_time.slice(0, 16).replace('T', ' ') }} · {{ t.position_side }}
          · {{ t.is_open ? 'open' : (t.realized_pnl >= 0 ? '+' : '') + t.realized_pnl.toFixed(2) }}
        </option>
      </select>
      <input v-if="!txn" v-model="date" type="date" class="form-control form-control-sm pm-date" @change="loadChart" />
      <select v-if="view === 'intraday'" v-model.number="sessions" class="form-select form-select-sm pm-n"
              @change="loadChart" title="Sessions">
        <option v-for="n in [1, 2, 3, 5, 10]" :key="n" :value="n">{{ n }} {{ n === 1 ? 'session' : 'sessions' }}</option>
      </select>
      <select v-else v-model.number="days" class="form-select form-select-sm pm-n" @change="loadChart"
              title="Daily sessions, ending at the trade's session (or the date)">
        <option v-for="d in DAY_CHOICES" :key="d.n" :value="d.n">{{ d.label }}</option>
      </select>
      <button class="btn btn-sm btn-primary" :disabled="loading">{{ loading ? 'Loading…' : 'Show' }}</button>
      <span class="pm-summary" v-if="summary">{{ summary }}</span>
      <span class="text-danger small" v-if="error">{{ error }}</span>
    </form>

    <MacroDailyChart
      v-if="view === 'macro' && macro"
      :ticker="macro.ticker"
      :candles="macro.candles"
      :ladders="macro.ladders || {}"
      :moving-averages="macro.moving_averages || {}"
      :averages="macroAverages"
      @update:averages="setMacroAverages"
      :oscillators="macro.oscillators || {}"
      :atr="macro.atr || []"
      :atr-period="macro.atr_period || 14"
      :markers="macro.markers || []"
      :height="760"
    />
    <div v-if="view === 'macro' && macro && macro.markers_error" class="text-muted small mt-1">
      Watch-list markers unavailable: {{ macro.markers_error }}
    </div>

    <AnnotatedCandleChart
      v-if="view === 'intraday' && chart"
      :ticker="chart.ticker"
      :candles="chart.candles"
      :sessions="chart.sessions"
      :ladders="ladders"
      :trade="chart.trade"
      :moving-averages="chart.moving_averages || {}"
      :averages="averages"
      @update:averages="setAverages"
      :oscillators="chart.oscillators || {}"
      :interval-minutes="chart.interval_minutes"
      :height="680"
    />
  </div>
</template>

<script>
/**
 * One chart and one line: the trade (or a session) on its own candles, with
 * each session's Camarilla ladder. The chart is AnnotatedCandleChart; this
 * view only picks what to load. The Macro view (MacroDailyChart) is the same
 * ticker on daily bars, ending at the trade's session (or the date): weekly and
 * monthly Camarilla ladders, the researcher's averages, the daily stochastic,
 * ATR14 and the watch list's decisions. Deep links:
 * ?ticker=&e2etransactionid=&date=&sessions=&view=macro&days=.
 */
import apiClient from '../services/apiClient';
import AnnotatedCandleChart, { DEFAULT_AVERAGES, MAX_AVERAGE_PERIOD } from '../components/AnnotatedCandleChart.vue';
import MacroDailyChart, { DEFAULT_MACRO_AVERAGES } from '../components/MacroDailyChart.vue';

const AVERAGES_KEY = 'postMortem.averages';
const MACRO_AVERAGES_KEY = 'macroChart.averages';
const DAY_CHOICES = [
  { n: 63, label: '3 months' }, { n: 126, label: '6 months' }, { n: 252, label: '1 year' },
  { n: 504, label: '2 years' }, { n: 756, label: '3 years' },
];

/** The viewer's moving-average periods, remembered per browser; the defaults when unreadable. */
function loadAverages(key = AVERAGES_KEY, defaults = DEFAULT_AVERAGES) {
  try {
    const stored = JSON.parse(window.localStorage.getItem(key));
    if (Array.isArray(stored) && stored.length === defaults.length
        && stored.every((a, i) => a && a.kind === defaults[i].kind && Number.isInteger(a.period)
                                  && a.period >= 1 && a.period <= MAX_AVERAGE_PERIOD)) {
      return stored.map((a) => ({ kind: a.kind, period: a.period }));
    }
  } catch (e) { /* unreadable storage: the defaults */ }
  return defaults.map((a) => ({ ...a }));
}

export default {
  name: 'PostMortem',
  components: { AnnotatedCandleChart, MacroDailyChart },
  data() {
    const q = this.$route.query;
    return {
      ticker: String(q.ticker || '').toUpperCase(),
      txn: String(q.e2etransactionid || ''),
      date: String(q.date || ''),
      sessions: Number(q.sessions) || 1,
      view: q.view === 'macro' ? 'macro' : 'intraday',
      days: DAY_CHOICES.some((d) => d.n === Number(q.days)) ? Number(q.days) : 252,
      averages: loadAverages(),
      macroAverages: loadAverages(MACRO_AVERAGES_KEY, DEFAULT_MACRO_AVERAGES),
      macro: null,
      DAY_CHOICES,
      transactions: [],
      chart: null,
      ladders: [],
      loading: false,
      error: null,
    };
  },
  computed: {
    /** The macro chart's last session: the trade's own, else the date, else today. */
    macroEnd() {
      const t = this.txn && this.transactions.find((x) => x.e2etransactionid === this.txn);
      if (t && t.first_trade_time) return t.first_trade_time.slice(0, 10);
      return this.date || null;
    },
    summary() {
      if (this.view === 'macro') {
        if (!this.macro) return '';
        const n = (this.macro.markers || []).length;
        return `${this.macro.ticker} · daily to ${this.macro.end} · ${n} watch-list ${n === 1 ? 'entry' : 'entries'}`;
      }
      const t = this.chart && this.chart.trade;
      if (!t) return this.chart ? `${this.chart.ticker} · no trade on the chart` : '';
      const parts = [t.side, t.setup ? t.setup.replace(/^camarilla_/, '').replace(/_/g, ' ').toUpperCase() : null,
                     t.population, t.planned_entry ? `level ${t.planned_entry.toFixed(2)}` : null,
                     t.fill && t.fill.price ? `fill ${t.fill.price.toFixed(2)}` : null,
                     t.exit && t.exit.rule ? `exit ${t.exit.rule}` : (t.open ? 'open' : null),
                     t.realized_pnl != null ? `P&L ${t.realized_pnl >= 0 ? '+' : ''}${t.realized_pnl.toFixed(2)}` : null];
      return parts.filter(Boolean).join(' · ');
    },
  },
  mounted() {
    if (this.ticker) this.loadTicker();
  },
  methods: {
    async loadTicker() {
      this.ticker = this.ticker.toUpperCase();
      this.transactions = [];
      try {
        const res = await apiClient.get(`/post_mortem/${this.ticker}/transactions`);
        this.transactions = res.data.transactions.filter((t) => t.e2etransactionid !== '__legacy__');
        if (this.txn && !this.transactions.some((t) => t.e2etransactionid === this.txn)) this.txn = '';
        if (!this.txn && !this.date && this.transactions.length) this.txn = this.transactions[0].e2etransactionid;
      } catch (err) {
        if (!err.response || err.response.status !== 404) this.error = err.message;
      }
      await this.loadChart();
    },
    setView(view) {
      if (view === this.view) return;
      this.view = view;
      this.loadChart();
    },
    setMacroAverages(averages) {
      this.macroAverages = averages;
      try { window.localStorage.setItem(MACRO_AVERAGES_KEY, JSON.stringify(averages)); } catch (e) { /* per-viewer nicety */ }
      this.loadChart();
    },
    async loadMacro() {
      const params = { days: this.days,
                       averages: this.macroAverages.map((a) => `${a.kind}${a.period}`).join(',') };
      if (this.macroEnd) params.end = this.macroEnd;
      this.macro = (await apiClient.get(`/macro_chart/${this.ticker}`, { params })).data;
    },
    setAverages(averages) {
      this.averages = averages;
      try { window.localStorage.setItem(AVERAGES_KEY, JSON.stringify(averages)); } catch (e) { /* per-viewer nicety */ }
      this.loadChart();
    },
    async loadChart() {
      if (!this.ticker) return;
      this.loading = true;
      this.error = null;
      try {
        if (this.view === 'macro') {
          await this.loadMacro();
          this.replaceQuery();
          return;
        }
        const params = { sessions: this.sessions,
                         averages: this.averages.map((a) => `${a.kind}${a.period}`).join(',') };
        if (this.txn) params.e2etransactionid = this.txn;
        else if (this.date) params.date = this.date;
        const chart = (await apiClient.get(`/post_mortem/${this.ticker}/chart`, { params })).data;
        const days = chart.sessions.map((s) => s.date);
        const levels = days.length
          ? (await apiClient.get('/camarilla/levels', {
              params: { ticker: this.ticker, start: days[0], end: days[days.length - 1] } })).data.sessions
          : [];
        this.ladders = levels;
        this.chart = chart;
        this.replaceQuery();
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.detail) || err.message;
      } finally {
        this.loading = false;
      }
    },
    replaceQuery() {
      this.$router.replace({ query: { ticker: this.ticker, ...(this.txn ? { e2etransactionid: this.txn } : {}),
                                      ...(!this.txn && this.date ? { date: this.date } : {}),
                                      sessions: String(this.sessions),
                                      ...(this.view === 'macro' ? { view: 'macro', days: String(this.days) } : {}) } });
    },
  },
};
</script>

<style scoped>
.pm-header { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem; }
.pm-ticker { width: 7rem; text-transform: uppercase; }
.pm-txn { width: auto; max-width: 22rem; }
.pm-date { width: 10rem; }
.pm-n { width: 8.5rem; }
.pm-summary { color: #495057; font-size: 0.9rem; font-variant-numeric: tabular-nums; margin-left: 0.5rem; }
</style>
